// src/pages/markets/board/api/metricEval.ts

import type { BoardScore } from './boardTypes';
import type { MetricDef, MetricKernel } from './metrics';

function readPath(score: BoardScore, path: string): unknown {
    if (path.startsWith('agg.')) {
        return score.agg?.[path.slice(4)];
    }
    return (score as unknown as Record<string, unknown>)[path];
}

function asNumber(value: unknown): number | null {
    if (value === null || value === undefined) return null;
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
}

// Any null operand makes the whole metric null. A missing capital leg is not
// zero — it means the field was not written, and showing $0 would be a lie.
export function evaluateKernel(kernel: MetricKernel, score: BoardScore): number | null {
    switch (kernel.kind) {
        case 'field':
            return asNumber(readPath(score, kernel.path));

        case 'sum':
        case 'max':
        case 'min': {
            const values: number[] = [];
            for (const child of kernel.of) {
                const v = evaluateKernel(child, score);
                if (v === null) return null;
                values.push(v);
            }
            if (values.length === 0) return null;
            if (kernel.kind === 'sum') return values.reduce((a, b) => a + b, 0);
            if (kernel.kind === 'max') return Math.max(...values);
            return Math.min(...values);
        }

        case 'diff': {
            const a = evaluateKernel(kernel.a, score);
            const b = evaluateKernel(kernel.b, score);
            if (a === null || b === null) return null;
            return a - b;
        }

        case 'ratio': {
            const a = evaluateKernel(kernel.a, score);
            const b = evaluateKernel(kernel.b, score);
            if (a === null || b === null || b === 0) return null;
            return a / b;
        }

        case 'abs': {
            const v = evaluateKernel(kernel.of, score);
            return v === null ? null : Math.abs(v);
        }

        default:
            return null;
    }
}

export function evaluate(def: MetricDef, score: BoardScore | null | undefined): number | null {
    if (!score || !def.kernel) return null;
    return evaluateKernel(def.kernel, score);
}

export function dominantSide(score: BoardScore): 'YES' | 'NO' {
    return score.capital_usd_yes >= score.capital_usd_no ? 'YES' : 'NO';
}

export function dominantCapital(score: BoardScore): number {
    return Math.max(score.capital_usd_yes, score.capital_usd_no);
}

export function traderCount(score: BoardScore): number {
    return score.n_traders_yes + score.n_traders_no;
}